<script setup lang="ts">
/**
 * Country / city list of the Search region dialog — the body of the desktop
 * popover (`Region and Sign out · states` D 11–14) and of the mobile second
 * sheet (M 11–14). Typing filters; the selected row carries the radio.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairRadioButtonIndicator } from '@/ui/primitives/FairRadioButton'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import {
  filterFairProfileV2RegionOptions,
  type FairProfileV2RegionOption,
} from './fair-profile-v2-settings.data'

interface FairProfileV2RegionListProps {
  options: FairProfileV2RegionOption[]
  selectedId?: string | null
  loading?: boolean
  error?: boolean
  emptyText: string
  /** Short fixed lists (People relation / age) have no search. */
  searchable?: boolean
  /** People menus (`07` / `08`): an empty radio on every row, filled on the pick. */
  radios?: boolean
}

const props = withDefaults(defineProps<FairProfileV2RegionListProps>(), {
  selectedId: null,
  loading: false,
  error: false,
  searchable: true,
  radios: false,
})

const emit = defineEmits<{
  select: [option: FairProfileV2RegionOption]
  retry: []
}>()

const { t } = useI18n()
const query = ref('')

const filtered = computed(() =>
  filterFairProfileV2RegionOptions(props.options, query.value),
)
</script>

<template>
  <div class="fair-profile-v2-region-list">
    <FairInputText
      v-if="searchable"
      v-model="query"
      class="fair-profile-v2-region-list__search"
      variant="underline"
      :placeholder="t('profile.settings.region.search')"
      :aria-label="t('profile.settings.region.search')"
    />

    <!-- 12 Country list · loading -->
    <div
      v-if="loading"
      class="fair-profile-v2-region-list__skeleton"
      aria-busy="true"
    >
      <FairSkeleton
        v-for="index in 4"
        :key="index"
        :width="index % 2 ? 120 : 88"
        :height="10"
      />
    </div>

    <!-- 13 Country list · error: neutral message + Try again -->
    <template v-else-if="error">
      <p class="fair-profile-v2-region-list__note">
        {{ t('profile.settings.region.list_error') }}
      </p>
      <FairButton
        class="fair-profile-v2-region-list__retry"
        :label="t('profile.settings.retry')"
        variant="tonal"
        size="auto"
        @click="emit('retry')"
      />
    </template>

    <template v-else-if="filtered.length">
      <button
        v-for="option in filtered"
        :key="option.id"
        type="button"
        class="fair-profile-v2-region-list__row"
        :class="{
          'fair-profile-v2-region-list__row--selected': option.id === selectedId,
        }"
        :aria-pressed="option.id === selectedId"
        @click="emit('select', option)"
      >
        <span>{{ option.label }}</span>
        <FairRadioButtonIndicator
          v-if="radios || option.id === selectedId"
          :selected="option.id === selectedId"
        />
      </button>
    </template>

    <!-- 09 no match: a quiet line, nothing else -->
    <p
      v-else
      class="fair-profile-v2-region-list__note"
    >
      {{ emptyText }}
    </p>
  </div>
</template>
